import { ListHeading } from "@/components/shared/list-header";
import { Input, RadioGroup, RadioGroupItem, View } from "@/components/ui";
import { ClipboardText } from "@/components/shared/icons";
import { Typography } from "@/components/shared/typography";
import { InputFormControl } from "@/components/shared/input-form-control";
import { type Control, Controller } from "react-hook-form";
import { type PetProfileFormData } from "./usePetProfileForm";
import { cn } from "@/lib/utils";
import { useClientServiceProfileByFiltersQuery } from "@/gqlhooks/profile/useClientServiceProfileByFiltersQuery";
import { Divider } from "@/components/shared/divider";
import { ListCheckboxInputItem } from "@/components/shared/list-checkbox-input";
import { petConditionOptions } from "./lib";
import { SalesModal } from "@/components/shared/booking/sales-modal";
import { useState } from "react";
import { generateBookingChatSalesMessage } from "@/lib/chat-sales";
import { useParams } from "react-router-dom";
import { ServiceName } from "@/types/service";
import { DEFAULT_WHATSAPP_PET_CARE_MESSAGE } from "./lib";

export const PetProfileForm = ({
  readonlyType = false,
  control,
}: {
  control: Control<PetProfileFormData>;
  readonlyType?: boolean;
}) => {
  const { name } = useParams<{ name: ServiceName }>();

  const whatsappMessage = name
    ? generateBookingChatSalesMessage(name)
    : DEFAULT_WHATSAPP_PET_CARE_MESSAGE;

  const [openChatSales, setOpenChatSales] = useState<boolean>(false);
  const { profiles } = useClientServiceProfileByFiltersQuery("pet");
  const isCatDisabled =
    profiles.filter((profile) => profile.visibleAttributes[0] === "Cat")
      .length >= 3;
  const isDogDisabled =
    profiles.filter((profile) => profile.visibleAttributes[0] === "Dog")
      .length >= 3;
  return (
    <View>
      <ListHeading title="Pet Profile" icon={ClipboardText} />
      <Typography
        color="foreground-intermediate"
        variant="body-md"
        className="pt-1"
      >
        Share a few details so our groomers can prep the perfect session for
        your furry friend
      </Typography>

      <View className="pt-4">
        <View className="pb-3">
          <Typography as="label" variant="label-sm" className="pb-1">
            Pet Type
          </Typography>
          <Controller
            name="type"
            control={control}
            render={({ field }) => (
              <RadioGroup
                value={field.value}
                onValueChange={field.onChange}
                className="flex flex-row gap-2"
                disabled={readonlyType}
              >
                <RadioItem
                  value="dog"
                  label="Dog"
                  disabled={isDogDisabled}
                  onLabelClick={() =>
                    !readonlyType && !isDogDisabled && field.onChange("dog")
                  }
                />
                <RadioItem
                  value="cat"
                  label="Cat"
                  disabled={isCatDisabled}
                  onLabelClick={() =>
                    !readonlyType && !isCatDisabled && field.onChange("cat")
                  }
                />
              </RadioGroup>
            )}
          />
        </View>

        <InputFormControl
          name="name"
          label="Pet Name"
          component={Input}
          control={control}
          componentProps={{
            placeholder: "e.g., Luna",
          }}
        />
        <InputFormControl
          name="breed"
          label="Breed"
          component={Input}
          control={control}
          componentProps={{
            placeholder: "e.g., Persian",
          }}
        />
        <InputFormControl
          name="age"
          label="Age"
          component={Input}
          control={control}
          componentProps={{
            placeholder: "e.g., 2 yrs",
          }}
        />

        <Typography as="label" variant="label-sm" className="pb-1">
          Gender
        </Typography>
        <Controller
          name="gender"
          control={control}
          render={({ field }) => (
            <RadioGroup
              value={field.value}
              onValueChange={field.onChange}
              className="flex flex-row gap-2"
            >
              <RadioItem
                value="male"
                label="Male"
                onLabelClick={() => field.onChange("male")}
              />

              <RadioItem
                value="female"
                label="Female"
                onLabelClick={() => field.onChange("female")}
              />
            </RadioGroup>
          )}
        />

        <Divider className="-mx-4 my-4 border-b-2 md:mx-0" />
        <PetSpecialConditionsListCheckbox
          control={control}
          onConditionChange={() => setOpenChatSales(true)}
        />
      </View>
      <SalesModal
        open={openChatSales}
        setOpen={setOpenChatSales}
        title="Need help with your Pet requirements?"
        description="If your pet has any of these special conditions, our care consultants can help match you with the right groomer. Chat with us on WhatsApp for support."
        whatsappMessage={whatsappMessage}
      />
    </View>
  );
};

const PetSpecialConditionsListCheckbox = ({
  control,
  onConditionChange,
}: {
  control: Control<PetProfileFormData>;
  onConditionChange: () => void;
}) => {
  const name = "special_conditions";

  return (
    <View className="pb-3">
      <Typography variant="label-sm" className="block pb-4">
        Does your pet fall under any of the following conditions? (Please tick
        all that apply)
      </Typography>

      {petConditionOptions.map((item, index) => (
        <Controller
          key={item.value}
          control={control}
          name={`${name}.${index}.selected`}
          render={({ field }) => (
            <ListCheckboxInputItem
              checked={field.value ?? false}
              onChange={(_, value) => {
                field.onChange(value);
                if (value) {
                  onConditionChange();
                }
              }}
              label={item.label}
            />
          )}
        />
      ))}
    </View>
  );
};

const RadioItem = ({
  value,
  label,
  disabled = false,
  onLabelClick,
}: {
  value: string;
  label: string;
  disabled?: boolean;
  onLabelClick: () => void;
}) => {
  return (
    <View
      className={cn(
        "border-border-intermediate flex flex-1 flex-row items-center justify-between rounded-lg border border-border p-2",
        disabled && "cursor-not-allowed opacity-50",
      )}
      onClick={disabled ? undefined : onLabelClick}
    >
      <Typography
        variant="body-sm"
        onClick={disabled ? undefined : onLabelClick}
      >
        {label}
      </Typography>
      <RadioGroupItem checkCircle value={value} disabled={disabled} />
    </View>
  );
};
